import { getSubscriptionStatus } from "api/subscriptions";
import { exploreTrees } from "api/trees";
import type { ExploreTreeDto, MatchedEntityDto, SubscriptionStatusDto } from "api/types";
import { LegalLinks } from "components";
import { useBrand } from "hooks/useBrand";
import { useSeo } from "hooks/useSeo";
import HomeSidebar from "pages/HomePage/components/HomeSidebar";
import { defaultCoverPreview } from "pages/HomePage/data";
import TopNav from "pages/TreeViewPage/TopNav";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Paths from "routes/Paths";
import { useSession } from "Session";
import { formatLastModified } from "utilities";

import { ExploreSearchBar, ExploreTreeCard } from "./components";

import type { ExploreCard } from "./explore.types";

import styles from "./ExplorePage.module.css";
import homeStyles from "pages/HomePage/HomePage.module.css";

const PAGE_SIZE = 24;

const getInitials = (name: string): string =>
	name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((word: string) => word[0]?.toUpperCase() ?? "")
		.join("") || "U";

const toCard = (tree: ExploreTreeDto, noDescription: string): ExploreCard => ({
	cover: tree.coverImageUrl?.trim() || defaultCoverPreview,
	description: tree.description?.trim() || noDescription,
	entityCount: tree.entityCount,
	id: tree.id,
	isLiked: tree.isLikedByMe,
	lastEditedLabel: formatLastModified(tree.lastModifiedDate),
	likes: tree.likeCount,
	matches: tree.matchedEntities.map((entity: MatchedEntityDto) => ({
		id: entity.id,
		label: entity.title,
		type: entity.entityType
	})),
	ownerImageUrl: tree.ownerProfileImageUrl,
	ownerInitials: getInitials(tree.ownerName),
	ownerName: tree.ownerName,
	ownerUserId: tree.ownerUserId,
	stars: tree.starCount,
	status: tree.visibility,
	title: tree.title
});

/**
 * Explore: searches every public tree — the user's own public trees included — by tree, person,
 * place, or any other entity name. Shows the latest public trees until a search is made. The backend
 * already supports Trending/New/ForYou feeds for the tabs planned once the audience grows.
 */
const ExplorePage = () => {
	const { t, ready } = useTranslation(["explorePage", "seo"]);
	// `t` changes identity as each namespace lands over HTTP. It only supplies fallback copy inside the
	// loader, so it is read through a ref: depending on it directly rebuilt the callback and refetched
	// the feed for nothing — now on a path search crawlers hit.
	const translate = useRef(t);
	translate.current = t;
	const { brand } = useBrand();

	// Now that it renders anonymously it is a real landing surface for search: the one page that lists
	// public trees, and the crawler's route from the site root into individual trees.
	// Namespaces arrive over HTTP. Until they land `t` echoes the raw key, so hold both back rather
	// than write "explore.title" into the tab and the meta description a crawler may be reading.
	useSeo({
		title: ready ? t("seo:explore.title") : null,
		description: ready ? t("seo:explore.description", { brand }) : null,
		canonicalPath: Paths.explore
	});
	const navigate = useNavigate();
	const { getAccessToken, hasSession, signOut, user } = useSession();
	const [searchInput, setSearchInput] = useState("");
	const [submittedSearch, setSubmittedSearch] = useState("");
	const [cards, setCards] = useState<ExploreCard[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [loadError, setLoadError] = useState("");
	const [pageIndex, setPageIndex] = useState(1);
	const [hasNextPage, setHasNextPage] = useState(false);
	const [subscription, setSubscription] = useState<SubscriptionStatusDto | null>(null);

	const displayName = user?.fullName ?? t("common:yourAccount");
	const initials = getInitials(user?.fullName ?? "");
	const isPro = subscription?.isPro ?? false;

	const loadPage = useCallback(
		async (search: string, page: number, append: boolean): Promise<void> => {
			if (append) {
				setIsLoadingMore(true);
			} else {
				setIsLoading(true);
			}
			setLoadError("");

			try {
				// Anonymous visitors get an empty token and the public pool; a signed-in caller also gets
				// their own private and shared trees back. Explore is the only page that lists trees, so
				// gating it would leave crawlers and signed-out readers no way to reach one.
				const token = await getAccessToken();

				const response = await exploreTrees(
					{ pageIndex: page, pageSize: PAGE_SIZE, search: search || undefined },
					token
				);

				const nextCards = response.items.map((tree: ExploreTreeDto) =>
					toCard(tree, translate.current("noDescription"))
				);

				setCards((current: ExploreCard[]) => (append ? [...current, ...nextCards] : nextCards));
				setPageIndex(response.pageIndex);
				setHasNextPage(response.hasNextPage);
			} catch (error) {
				if (!append) {
					setCards([]);
				}
				setLoadError(error instanceof Error ? error.message : translate.current("loadError"));
			} finally {
				setIsLoading(false);
				setIsLoadingMore(false);
			}
		},
		[getAccessToken]
	);

	// The latest public trees greet every visitor, signed in or not, until they search for something
	// specific. Re-runs on sign-in so the caller's own trees join the pool.
	useEffect(() => {
		loadPage(submittedSearch, 1, false).catch(() => undefined);
	}, [hasSession, loadPage, submittedSearch]);

	// Pro status only feeds the sidebar badge; failures just keep the upgrade button visible.
	useEffect(() => {
		if (!hasSession) {
			setSubscription(null);

			return undefined;
		}

		let isCurrent = true;

		const loadSubscription = async (): Promise<void> => {
			try {
				const token = await getAccessToken();
				if (!token || !isCurrent) {
					return;
				}

				const status = await getSubscriptionStatus(token);
				if (isCurrent) {
					setSubscription(status);
				}
			} catch {
				// Keep the upgrade button when the status lookup fails.
			}
		};

		loadSubscription().catch(() => undefined);

		return () => {
			isCurrent = false;
		};
	}, [getAccessToken, hasSession]);

	const onUpgradeToPro = useCallback((): void => navigate(Paths.plans), [navigate]);

	const onSearch = useCallback((term: string): void => {
		setSubmittedSearch(term);
	}, []);

	const onLoadMore = useCallback((): void => {
		loadPage(submittedSearch, pageIndex + 1, true).catch(() => undefined);
	}, [loadPage, pageIndex, submittedSearch]);

	let message = "";

	if (loadError) {
		message = loadError;
	} else if (cards.length === 0) {
		if (isLoading) {
			message = t("loading");
		} else if (submittedSearch) {
			message = t("emptySearch", { term: submittedSearch });
		} else {
			message = t("emptyFeed");
		}
	}

	const content = (
		<>
			<header className={styles.header}>
				<h1 className={styles.title}>{t("title")}</h1>
				<p className={styles.subtitle}>{t("subtitle")}</p>
				<ExploreSearchBar
					isSearching={isLoading}
					onSearch={onSearch}
					onValueChange={setSearchInput}
					value={searchInput}
				/>
			</header>

			<h2 className={styles.resultsHeading}>
				{submittedSearch ? t("resultsFor", { term: submittedSearch }) : t("latestTrees")}
			</h2>

			{message ? (
				<p className={styles.message}>{message}</p>
			) : (
				<>
					<section aria-label={t("resultsLabel")} className={styles.resultsGrid}>
						{cards.map((card: ExploreCard) => (
							<ExploreTreeCard key={card.id} card={card} />
						))}
					</section>

					{hasNextPage && (
						<div className={styles.loadMoreRow}>
							<button
								className={styles.loadMoreButton}
								disabled={isLoadingMore}
								onClick={onLoadMore}
								type="button">
								{isLoadingMore ? t("loadingMore") : t("loadMore")}
							</button>
						</div>
					)}
				</>
			)}
		</>
	);

	// Signed-out readers get the public site chrome: every sidebar destination but Explore needs an
	// account, so the rail was a column of dead ends. The header carries Sign in / Sign up instead.
	if (!hasSession) {
		return (
			<div className={styles.publicShell}>
				<TopNav />
				<main className={styles.publicMain}>{content}</main>
				<footer className={styles.footer}>
					<span>{t("common:copyright")}</span>
					<nav aria-label={t("common:sitePagesLabel")} className={styles.footerNav}>
						<LegalLinks />
					</nav>
				</footer>
			</div>
		);
	}

	return (
		<div className={homeStyles.shell}>
			<HomeSidebar
				displayName={displayName}
				initials={initials}
				isPro={isPro}
				onSignOut={signOut}
				onUpgrade={onUpgradeToPro}
				profileImageUrl={user?.profileImageUrl}
			/>

			<main className={homeStyles.main}>{content}</main>
		</div>
	);
};

export default ExplorePage;
